import axios from 'axios';
import {
  LIST_QUESTIONNAIRE_FORM,
  GET_FORM,
  get_headers,
  SHARE_LEAD_FORM,
  GET_LEAD_FORMS
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin.js';

export default {
  name: 'ShareFormsWithLead',

  data: () => ({
    headers: get_headers(),
    project_id: sessionStorage.getItem('project_id'),
    forms: [],
    email: '',
    lead_id: sessionStorage.getItem('lead_id') || null,
    entity_type_id: sessionStorage.getItem('entity_type_id') || null,
    showPreview: false,
    preview_form: { sections: []},
    preview_open_sections: []
  }),

  mixins: [ alert_mixin ],

  mounted() {
    if (!this.lead_id) {
      this.show_error('Missing lead ID in session.')
      return
    }
    this.getForms()
      .then(() => this.get_shared_forms())
  },

  watch: {
  'preview_form.sections': {
    handler(sections) {
      if (sections && sections.length > 0) {
        // Initialize all sections as open
        this.preview_open_sections = sections.map(() => true);
      }
    },
    immediate: true,
    deep: true
  }
},

  methods: {
    async getForms() {
    axios
      .get(LIST_QUESTIONNAIRE_FORM, {
        headers: this.headers,
        params: { project_id: this.project_id },
      })
      .then((resp) => {
        let payload = resp.data;
  
        if (typeof payload === 'string') {
          payload = JSON.parse(payload);
        }
  
        if (payload.status !== 'success' || !Array.isArray(payload.data)) {
          throw new Error('Unexpected payload format');
        }
  
        this.forms = payload.data.map((f) => ({
          id: f.id,
          name: f.form_name,
          form_description: f.form_description || '',
          status: f.status,
          selected: false,
        }));
      })
      .catch((err) => {
        this.show_error('Error fetching forms', err.message);
      });
   },

    async view_form(form) {
      const resp = await axios
      .get(GET_FORM, { headers: this.headers, params: { form_id: form.id } })
      .catch(err => {
        this.show_error('Fetch error', err.message);
        return null;
      });

      if (!resp) return;
      let payload = resp.data
      if (typeof payload === 'string') payload = JSON.parse(payload)

        if (payload.status === 'success') {
          this.preview_form = payload.data
          this.showPreview = true
        } else {
          this.show_error(payload.message || 'Could not load form')
        }
    },

    async get_shared_forms() {
      const raw_id = this.$route.query.lead_id;
      try {
        const resp = await axios.get(GET_LEAD_FORMS, {
          headers: this.headers,
          params: { lead_id: raw_id }
        });
        
        let payload = resp.data;
        if (typeof payload === 'string') payload = JSON.parse(payload);

        if (payload.status !== 'success') {
          if (payload.message && payload.message.includes('No forms shared')) {
            this.sharedFormIds = [];
            this.forms.forEach(f => f.selected = false);
            return;
          }
          throw new Error(payload.message || 'Unexpected response');
        }

        if (!Array.isArray(payload.data)) {
          throw new Error('Invalid response format');
        }

        this.sharedFormIds = payload.data.map(x => x.form_id);
        this.forms.forEach(f => {
          f.selected = this.sharedFormIds.includes(f.id);
        });

      } catch (err) {
        if (err.response?.data?.message?.includes('No forms shared')) {
          this.sharedFormIds = [];
          this.forms.forEach(f => f.selected = false);
          return;
        }

        if (!err.response || !err.response.data?.message?.includes('No forms shared')) {
          this.show_error('Error fetching shared forms', 
            err.response?.data?.message || err.message || 'Unknown error');
        }
      }
    },
    async get_form_to_preview() {
      const { data: { data } } = await axios.get(GET_FORM, {
        headers: this.headers,
        params: { form_id: this.form_id }
      });

      this.preview_sections = (data?.sections || []).map(section => ({
        id: section.id,
        name: section.name,
        order: section.order_number,
        questions: (section.questions || [])
          .filter(q => q.is_assigned)
          .filter(q => !this.removed_question_ids.includes(q.id))
          .map(question => {
            const data_point = question.key_data_points?.[0] || null;
            const data_point_id = data_point?.id || null;
            const tag = question.tag || null;
            const available_tags = this.get_tags_for_data_point(data_point_id);
            const selected_tag = available_tags.find(t => t.id === tag?.id)?.id || null;

            return {
              id: question.id,
              question_text: question.question_text,
              question_type: {
                question_type: question.question_type.question_type,
                id: question.question_type.id,
              },
              is_mandatory: question.is_mandatory,
              question_number: question.question_number,
              options: question.options || [],
              multi_select_choices: question.multi_select_choices || [],
              data_point,
              data_point_id,
              tag: tag
                ? {
                    id: tag.id,
                    tag_name: tag.tag_name,
                    data_point_id: tag.data_point_id,
                  }
                : null,
              tag_id: tag?.id || null,
              tag_name: tag?.tag_name || null,
              available_tags,
              selected_tag,
            };
          }),
      }));
    },

    formatType(t) {
      return t.replace('_',' ').toUpperCase()
    },

    async send_selected() {
      const selected_ids = this.forms
        .filter((f) => f.selected)
        .map((f) => f.id);

      if (!selected_ids.length) {
        this.show_error('Select at least one form');
        return;
      }

      const raw_lead =this.$route.query.lead_id;
      const parsed_lead = raw_lead !== null ? Number(raw_lead) : NaN;

      if (isNaN(parsed_lead) && !this.email.trim()) {
        this.show_error(
          'Missing Information',
          'Either lead_id must be in sessionStorage or an email must be provided'
        );
        return;
      }

      const payload = { form_ids: selected_ids,
        project_id: this.project_id,
        entity_type_id: this.entity_type_id,
       };

      if (!isNaN(parsed_lead)) {
        payload.lead_id = parsed_lead;
      }

      if (this.email.trim()) {
        payload.email = this.email.trim();
      }

      try {
        const resp = await axios.post(SHARE_LEAD_FORM, payload, {
          headers: { ...this.headers, 'Content-Type': 'application/json' },
        });

        let data = resp.data;
        if (typeof data === 'string') {
          data = JSON.parse(data);
        }

        if (data.status === 'success') {
          this.show_success('Forms shared successfully');
          await this.get_shared_forms()
          this.email = '';
        } else {
          this.show_error('Error', data.message || 'Unknown error');
        }
      } catch (err) {
        const msg =
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : err.message;
        this.show_error('Request failed', msg);
      }
    },

      toggle_preview_section(index) {
    const current = Reflect.get(this.preview_open_sections, index);
    Reflect.set(this.preview_open_sections, index, !current);
  },

  is_preview_section_open(index) {
    return Reflect.get(this.preview_open_sections, index) || false;
  }
  },
};
